interface JsonLdProps {
  data: Record<string, any> | Record<string, any>[];
}

export function JsonLd({ data }: JsonLdProps) {
  const jsonString = JSON.stringify(data);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonString }}
    />
  );
}

// Organization schema for the homepage
export function OrganizationJsonLd() {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "LoanGorilla",
        url: "https://loangorilla.com.au",
        description:
          "Australia's car loan comparison platform. Compare rates from 30+ lenders and find the best car loan for your needs.",
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "customer service",
          availableLanguage: "English",
        },
      }}
    />
  );
}

// LocalBusiness / FinancialService schema for homepage
export function LocalBusinessJsonLd() {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "FinancialService",
        name: "LoanGorilla",
        url: "https://loangorilla.com.au",
        description:
          "Australia's specialist car loan comparison and broking service. Compare rates from 40+ lenders and get expert help finding the best car loan.",
        areaServed: {
          "@type": "Country",
          name: "Australia",
        },
        serviceType: "Car Loan Comparison and Broking",
        knowsAbout: [
          "Car Loans",
          "Vehicle Finance",
          "Car Loan Refinancing",
          "Electric & Green Car Loans",
          "Bad Credit Car Loans",
          "Secured Car Loans",
          "Unsecured Car Loans",
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Car Loan Products",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "New Car Loan Comparison" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Used Car Loan Comparison" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Green Car Loan Comparison" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Car Loan Refinancing" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Bad Credit Car Finance" } },
          ],
        },
      }}
    />
  );
}

// WebSite schema with search action
export function WebSiteJsonLd() {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "LoanGorilla",
        url: "https://loangorilla.com.au",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://loangorilla.com.au/car-loans?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      }}
    />
  );
}

// FAQ schema
interface FaqItem {
  question: string;
  answer: string;
}

export function FAQPageJsonLd({ faqs }: { faqs: FaqItem[] }) {
  if (!faqs.length) return null;
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      }}
    />
  );
}

// Article schema for guide/blog pages
interface ArticleJsonLdProps {
  title: string;
  description?: string;
  author?: string;
  publishedAt?: string;
  updatedAt?: string;
  url: string;
}

export function ArticleJsonLd({
  title,
  description,
  author,
  publishedAt,
  updatedAt,
  url,
}: ArticleJsonLdProps) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: title,
        ...(description && { description }),
        author: author
          ? { "@type": "Person", name: author }
          : { "@type": "Organization", name: "LoanGorilla" },
        publisher: {
          "@type": "Organization",
          name: "LoanGorilla",
          url: "https://loangorilla.com.au",
        },
        url,
        ...(publishedAt && { datePublished: publishedAt }),
        ...(updatedAt && { dateModified: updatedAt }),
        inLanguage: "en-AU",
        about: { "@type": "Thing", name: "Car Loans Australia" },
      }}
    />
  );
}

// BreadcrumbList schema
interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbListJsonLd({ items }: { items: BreadcrumbItem[] }) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: item.url,
        })),
      }}
    />
  );
}

// HowTo schema for calculator pages
interface HowToStep {
  name: string;
  text: string;
}

interface HowToJsonLdProps {
  name: string;
  description: string;
  steps: HowToStep[];
  url: string;
}

export function HowToJsonLd({ name, description, steps, url }: HowToJsonLdProps) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "HowTo",
        name,
        description,
        url,
        inLanguage: "en-AU",
        step: steps.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.name,
          text: s.text,
        })),
      }}
    />
  );
}

// FinancialProduct schema for loan products
interface LoanProductJsonLdProps {
  name: string;
  description?: string;
  lenderName?: string;
  interestRate?: number | null;
  comparisonRate?: number | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  minTermMonths?: number | null;
  maxTermMonths?: number | null;
  establishmentFee?: number | null;
  monthlyFee?: number | null;
  annualFee?: number | null;
  url: string;
}

export function FinancialProductJsonLd({
  name,
  description,
  lenderName,
  interestRate,
  comparisonRate,
  minAmount,
  maxAmount,
  minTermMonths,
  maxTermMonths,
  establishmentFee,
  monthlyFee,
  annualFee,
  url,
}: LoanProductJsonLdProps) {
  const feeParts: string[] = [];
  if (establishmentFee != null && establishmentFee > 0) feeParts.push(`Establishment fee: $${establishmentFee}`);
  if (monthlyFee != null && monthlyFee > 0) feeParts.push(`Monthly fee: $${monthlyFee}`);
  if (annualFee != null && annualFee > 0) feeParts.push(`Annual fee: $${annualFee}`);

  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "LoanOrCredit",
        name,
        description: description || `${name} car loan from ${lenderName}`,
        loanType: "Car Loan",
        provider: lenderName
          ? { "@type": "Organization", name: lenderName }
          : undefined,
        url,
        category: "Auto Loan",
        ...(interestRate != null && {
          interestRate: {
            "@type": "QuantitativeValue",
            value: interestRate,
            unitCode: "P1",
            description: "Annual interest rate percentage",
          },
        }),
        ...(comparisonRate != null && {
          additionalProperty: {
            "@type": "PropertyValue",
            name: "Comparison Rate",
            value: `${comparisonRate}%`,
            description: "Comparison rate p.a. based on $30,000 over 5 years",
          },
        }),
        ...(minAmount != null && maxAmount != null && {
          amount: {
            "@type": "MonetaryAmount",
            currency: "AUD",
            minValue: minAmount,
            maxValue: maxAmount,
          },
        }),
        ...(minTermMonths != null && maxTermMonths != null && {
          loanTerm: {
            "@type": "QuantitativeValue",
            minValue: minTermMonths,
            maxValue: maxTermMonths,
            unitCode: "MON",
          },
        }),
        ...(feeParts.length > 0 && {
          feesAndCommissionsSpecification: feeParts.join(". "),
        }),
        currency: "AUD",
        areaServed: { "@type": "Country", name: "AU" },
      }}
    />
  );
}

// Dataset schema for structured data pages (comparison tables, FAQs, etc.)
interface DatasetJsonLdProps {
  name: string;
  description: string;
  url: string;
  keywords?: string[];
  dateModified?: string;
  recordCount?: number;
  spatialCoverage?: string;
}

export function DatasetJsonLd({
  name,
  description,
  url,
  keywords,
  dateModified,
  recordCount,
  spatialCoverage = "Australia",
}: DatasetJsonLdProps) {
  return (
    <JsonLd
      data={{
        "@context": "https://schema.org",
        "@type": "Dataset",
        name,
        description,
        url,
        creator: {
          "@type": "Organization",
          name: "LoanGorilla",
          url: "https://loangorilla.com.au",
        },
        license: "https://loangorilla.com.au/terms-of-use",
        inLanguage: "en-AU",
        spatialCoverage,
        ...(keywords?.length && { keywords: keywords.join(", ") }),
        ...(dateModified && { dateModified }),
        ...(recordCount && {
          variableMeasured: [
            { "@type": "PropertyValue", name: "Number of Products", value: recordCount },
          ],
        }),
      }}
    />
  );
}

// Collection of financial products for comparison pages
interface LoanProductData {
  id: string;
  product_name: string;
  description?: string | null;
  advertised_rate?: number | null;
  comparison_rate?: number | null;
  min_loan_amount?: number | null;
  max_loan_amount?: number | null;
  min_term_months?: number | null;
  max_term_months?: number | null;
  establishment_fee?: number | null;
  monthly_fee?: number | null;
  annual_fee?: number | null;
  slug: string;
  lenders?: { name: string } | null;
}

export function LoanProductListJsonLd({
  products,
  pageTitle,
  pageDescription,
  pageUrl,
}: {
  products: LoanProductData[];
  pageTitle: string;
  pageDescription: string;
  pageUrl: string;
}) {
  if (!products.length) return null;

  const buildFees = (p: LoanProductData) => {
    const parts: string[] = [];
    if (p.establishment_fee != null && p.establishment_fee > 0) parts.push(`Establishment: $${p.establishment_fee}`);
    if (p.monthly_fee != null && p.monthly_fee > 0) parts.push(`Monthly: $${p.monthly_fee}`);
    if (p.annual_fee != null && p.annual_fee > 0) parts.push(`Annual: $${p.annual_fee}`);
    return parts.length ? parts.join(". ") : undefined;
  };

  return (
    <>
      {/* Page-level FinancialProduct wrapper */}
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "FinancialProduct",
          name: "Car Loans Australia",
          description: pageDescription,
          url: pageUrl,
          category: "Auto Loan",
          areaServed: { "@type": "Country", name: "AU" },
          provider: { "@type": "Organization", name: "LoanGorilla", url: "https://loangorilla.com.au" },
        }}
      />
      {/* ItemList with LoanOrCredit items */}
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: pageTitle,
          description: pageDescription,
          url: pageUrl,
          numberOfItems: products.length,
          itemListElement: products.slice(0, 20).map((p, i) => {
            const lenderName = (p.lenders as any)?.name;
            const fees = buildFees(p);
            return {
              "@type": "ListItem",
              position: i + 1,
              item: {
                "@type": "LoanOrCredit",
                name: p.product_name,
                loanType: "Car Loan",
                description: p.description || `${p.product_name} from ${lenderName}`,
                provider: lenderName
                  ? { "@type": "Organization", name: lenderName }
                  : undefined,
                url: `https://loangorilla.com.au/car-loans/${p.slug}`,
                category: "Auto Loan",
                currency: "AUD",
                ...(p.advertised_rate != null && {
                  interestRate: {
                    "@type": "QuantitativeValue",
                    value: p.advertised_rate,
                    unitCode: "P1",
                  },
                }),
                ...(p.min_loan_amount != null && p.max_loan_amount != null && {
                  amount: {
                    "@type": "MonetaryAmount",
                    currency: "AUD",
                    minValue: p.min_loan_amount,
                    maxValue: p.max_loan_amount,
                  },
                }),
                ...(p.min_term_months != null && p.max_term_months != null && {
                  loanTerm: {
                    "@type": "QuantitativeValue",
                    minValue: p.min_term_months,
                    maxValue: p.max_term_months,
                    unitCode: "MON",
                  },
                }),
                ...(fees && { feesAndCommissionsSpecification: fees }),
              },
            };
          }),
        }}
      />
    </>
  );
}
